import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  CircularProgress,
  Fade,
  Paper
} from '@mui/material';
import { 
  Fingerprint as FingerprintIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const FingerprintAuth = ({ open, onSuccess, onCancel, title = "Fingerprint Authentication" }) => {
  const [scanState, setScanState] = useState('initial'); // initial, scanning, success, error
  const [scanProgress, setScanProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setScanState('initial');
      setScanProgress(0);
      setErrorMessage('');
    }
  }, [open]);
  
  // Auto-start scan when dialog opens
  useEffect(() => {
    if (open && scanState === 'initial') {
      handleStartScan();
    }
  }, [open, scanState]);
  
  const handleStartScan = () => {
    setScanState('scanning');
    setScanProgress(0);
    
    // Simulate fingerprint scanning with progress
    const interval = setInterval(() => {
      setScanProgress(prev => {
        const newProgress = prev + Math.random() * 15;
        
        if (newProgress >= 100) {
          clearInterval(interval);
          // 90% chance of success, 10% chance of failure (for demo purposes)
          const success = Math.random() > 0.1;
          
          setTimeout(() => {
            if (success) {
              setScanState('success');
              setTimeout(() => {
                if (typeof onSuccess === 'function') {
                  onSuccess();
                }
              }, 1000);
            } else {
              setScanState('error');
              setErrorMessage('Fingerprint not recognized. Please try again.');
            }
          }, 500);
          
          return 100;
        }
        
        return newProgress;
      });
    }, 100);
    
    return () => clearInterval(interval);
  };
  
  const handleRetry = () => {
    setScanState('initial');
  };
  
  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel();
    }
    // Reset state after a small delay
    setTimeout(() => {
      setScanState('initial');
      setScanProgress(0);
      setErrorMessage('');
    }, 300);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={scanState !== 'scanning' ? handleCancel : undefined}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 10002 }} // Ensure dialog appears above other components
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
        {scanState === 'initial' && (
          <Box sx={{ textAlign: 'center' }}>
            <Paper 
              elevation={3} 
              sx={{ 
                width: 120, 
                height: 120, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: '50%',
                mb: 2,
                mx: 'auto',
                bgcolor: 'primary.50'
              }}
            >
              <FingerprintIcon sx={{ fontSize: 70, color: 'primary.main' }} />
            </Paper>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Place your finger on the scanner to authenticate
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleStartScan}
              startIcon={<FingerprintIcon />}
            >
              Start Scan
            </Button>
          </Box>
        )}
        
        {scanState === 'scanning' && (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', mb: 3 }}>
              <CircularProgress 
                variant="determinate" 
                value={scanProgress} 
                size={120}
                thickness={4}
                sx={{ color: 'primary.main' }}
              />
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FingerprintIcon sx={{ fontSize: 70, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </Box>
            <Typography variant="body1">
              Scanning fingerprint... {Math.round(scanProgress)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Keep your finger on the scanner
            </Typography>
          </Box>
        )}
        
        {scanState === 'success' && (
          <Box sx={{ textAlign: 'center' }}>
            <Fade in={true}>
              <Paper 
                elevation={3} 
                sx={{ 
                  width: 120, 
                  height: 120, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  borderRadius: '50%',
                  mb: 2,
                  mx: 'auto',
                  bgcolor: 'success.50'
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 70, color: 'success.main' }} />
              </Paper>
            </Fade>
            <Typography variant="body1" color="success.main" sx={{ fontWeight: 500 }}>
              Authentication Successful
            </Typography>
          </Box>
        )}
        
        {scanState === 'error' && (
          <Box sx={{ textAlign: 'center' }}>
            <Fade in={true}>
              <Paper 
                elevation={3} 
                sx={{ 
                  width: 120, 
                  height: 120, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  borderRadius: '50%',
                  mb: 2,
                  mx: 'auto',
                  bgcolor: 'error.50'
                }}
              >
                <ErrorIcon sx={{ fontSize: 70, color: 'error.main' }} />
              </Paper>
            </Fade>
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleRetry}
              startIcon={<FingerprintIcon />}
            >
              Try Again
            </Button>
          </Box>
        )}
      </DialogContent>
      
      {scanState !== 'scanning' && scanState !== 'success' && (
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FingerprintAuth;
